import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { connection, useConnection } from '../offline/connection';
import { pinNeedsRegistration } from '../offline/state';
import { bannerDetail, waitingText } from '../offline/text';
import '../styles/offline.css';

/**
 * Persistent offline banner, rendered inside the fixed Header so it sits on every screen and the layout (which measures the header)
 * moves down with it. Offline: what that means for orders, how many are waiting to upload, "Go online" when the host lets the page
 * switch a manual offline mode off, and the PIN-login warning. Online with the engine present: only the PIN-login warning, when staff
 * login is on and this device holds no PIN credentials. With no engine it renders nothing.
 */
const OfflineBanner = ({ onNavigate }) => {
  const { state, offline, canForce } = useConnection();
  const needsPin = pinNeedsRegistration(state);
  const parked = state.parkedCount;
  if (!offline && !needsPin && parked === 0 && !state.uploadNeedsLogin) return null;

  const link = (label) => (
    <Link className="offlineBannerBtn" to="/offline" onClick={onNavigate}>
      {label}
    </Link>
  );

  if (!offline) {
    // online: only things somebody has to act on (orders the server refused, a session that must sign in again, PIN login not set up)
    const [title, detail, action] =
      parked > 0
        ? [`${parked} offline order${parked === 1 ? ' needs' : 's need'} attention`, 'The server did not accept them. They stay on this device until a manager retries or discards them.', 'Review']
        : state.uploadNeedsLogin
          ? ['Sign in again to upload offline orders', 'Orders taken offline are waiting for a valid session.', 'Details']
          : ['PIN login needs this device registered', 'Without it, staff PINs will not work if the internet drops.', 'Set up'];
    return (
      <div className="offlineBanner offlineBannerHint" role="status" aria-live="polite" data-testid="offline-banner">
        <div className="offlineBannerText">
          <strong>{title}</strong>
          <span>{detail}</span>
        </div>
        {link(action)}
      </div>
    );
  }

  const goOnline = async () => {
    if (!(await connection.setForced(false))) toast.error('Could not switch offline mode off.');
  };
  const waiting = waitingText(state);
  return (
    <div className="offlineBanner" role="status" aria-live="polite" data-testid="offline-banner">
      <div className="offlineBannerText">
        <strong>Offline mode</strong>
        <span>{bannerDetail(state.mode)}</span>
        {needsPin && <span className="offlineBannerWarn">PIN login needs this device registered.</span>}
      </div>
      {waiting && <span className="offlineBannerCount">{waiting}</span>}
      {state.parkedCount > 0 && <span className="offlineBannerCount offlineBannerCountBad">{state.parkedCount} need attention</span>}
      {state.mode === 'forced_offline' && canForce && (
        <button type="button" className="offlineBannerBtn" onClick={goOnline}>
          Go online
        </button>
      )}
      {link(needsPin ? 'Set up' : 'Details')}
    </div>
  );
};

export default OfflineBanner;
